/* eslint-disable no-undef */
/**
 * Communication bridge with ScreenApp Chrome Extension for recovery features
 */

import { config } from '../../config';
import logger from './logger';

const EXTENSION_ID = config.extensionId;

export interface ExtensionBackupRecord {
  fileId: string;
  uploadId?: string;
  teamId?: string;
  folderId?: string;
  startedAt: number;
  status: 'pending' | 'dismissed' | 'uploaded' | 'recovered';
  contentType?: string;
  title?: string;
}

/**
 * Check if the ScreenApp extension is installed
 */
export function isExtensionInstalled(): boolean {
  return typeof chrome !== 'undefined' && !!chrome.runtime;
}

/**
 * Send a message to the extension and get a response
 */
function sendMessageToExtension<T = any>(message: any): Promise<T> {
  return new Promise((resolve, reject) => {
    if (!isExtensionInstalled()) {
      reject(new Error('Extension not installed'));
      return;
    }

    try {
      chrome.runtime.sendMessage(EXTENSION_ID, message, (response) => {
        if (chrome.runtime.lastError) {
          logger.warn('Extension message error:', chrome.runtime.lastError);
          reject(new Error(chrome.runtime.lastError.message));
        } else if (response?.success === false) {
          reject(new Error(response.error || 'Extension request failed'));
        } else {
          resolve(response);
        }
      });
    } catch (error) {
      logger.error('Failed to send message to extension:', error);
      reject(error);
    }
  });
}

/**
 * Get all backup records from the extension
 */
export async function getExtensionBackupRecords(): Promise<
  Record<string, ExtensionBackupRecord>
> {
  try {
    const response = await sendMessageToExtension<{
      success: boolean;
      records: Record<string, ExtensionBackupRecord>;
    }>({
      action: 'getExtensionBackupRecords',
    });

    return response.records || {};
  } catch (error) {
    logger.error('Failed to get extension backup records:', error);
    return {}; // Return empty object if extension not installed or error
  }
}

/**
 * Trigger re-upload of a recording from the extension
 */
export async function triggerExtensionRecoveryReupload(params: {
  fileId: string;
  teamId: string;
  folderId: string;
  uploadId: string;
  contentType: string;
}): Promise<any> {
  const response = await sendMessageToExtension({
    action: 'triggerRecoveryReupload',
    ...params,
  });

  return response;
}

/**
 * Delete a backup record from the extension
 */
export async function deleteExtensionBackupRecord(
  fileId: string,
): Promise<void> {
  await sendMessageToExtension({
    action: 'deleteExtensionBackupRecord',
    fileId,
  });
}

/**
 * Get count of pending recovery records from extension
 */
export async function getExtensionRecoveryCount(): Promise<number> {
  try {
    const records = await getExtensionBackupRecords();
    return Object.values(records).filter((r) => r.status === 'pending').length;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return 0;
  }
}
